import { useMemo, useState } from "react";
import { SHOW_MORE_STEP, clampShownCount } from "../lib/pagination";

/**
 * Accumulating "show more" pagination — the load-more counterpart to
 * usePagination's prev/next paging. Starts by showing one `step`, and each
 * showMore() reveals the next `step` items until the whole list is visible.
 * All slicing is client-side; the caller keeps loading the full list.
 */
export function useShowMore(items, { step = SHOW_MORE_STEP } = {}) {
  const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const total = list.length;
  const [visibleCount, setVisibleCount] = useState(step);

  const shownCount = clampShownCount(visibleCount, total, step);

  // Keep stored state in lockstep with the clamped count. Without this, a live
  // list that shrinks (a group deleted / left) below the current count would
  // leave storedCount stale — making the first Show more a dead no-op, or
  // silently re-expanding when the list grows again. Adjusting state during
  // render (the documented React reset pattern) is synchronous and self-disarms
  // once storedCount === shownCount, so it commits no render loop.
  if (visibleCount !== shownCount) {
    setVisibleCount(shownCount);
  }

  const visibleItems = useMemo(
    () => list.slice(0, shownCount),
    [list, shownCount]
  );

  return {
    visibleItems,
    shownCount,
    total,
    remaining: Math.max(0, total - shownCount),
    hasMore: shownCount < total,
    // Grow from the already-clamped shownCount, not the raw stored count, so a
    // single click always adds a batch even right after a live shrink.
    showMore: () =>
      setVisibleCount(clampShownCount(shownCount + step, total, step)),
    step,
  };
}
